/**
 * SpaceContext store — tracks the active drawing space and the
 * paper/model context mode, mirroring AutoCAD's TILEMODE / MSPACE /
 * PSPACE / MSPACE_VIA_VP distinction.
 *
 * Three modes (AutoCAD parity):
 *   MSPACE       — Model space active (tiled or single viewport)
 *   PSPACE       — Paper space (layout tab) active, editing the layout
 *   MSPACE_VIA_VP— Paper space active, but user double-clicked a
 *                  floating viewport to edit model geometry through it
 *
 * Lifecycle:
 *   - Initialized by App.tsx on mount + whenever geometryDocument.spaces
 *     changes (document load / DXF import).
 *   - Synced from editorStore when shell/activate-space or layoutContext
 *     changes (via useEffect in App.tsx).
 *   - enterViewport / exitViewport handle the double-click-viewport UX
 *     (MC3 will wire these to Scene3DStage viewport pick events).
 */

import { create } from "zustand";

export type SpaceContextMode = "MSPACE" | "PSPACE" | "MSPACE_VIA_VP";

export interface SpaceContextState {
  /** Id of the currently active space (model / layoutN / …). */
  activeSpaceId: string;
  /** Null unless mode is MSPACE_VIA_VP. */
  activeViewportId: string | null;
  /** Current drawing-context mode. */
  mode: SpaceContextMode;
  /** Tab order for the SpaceTabsBar. Persisted across command submissions. */
  layoutOrder: string[];
  /** Document id passed to the last initFromSpaces call — used to detect new-document imports. */
  lastInitDocumentId: string | null;

  /** Switch to a different space tab. */
  setActiveSpace: (spaceId: string) => void;
  /**
   * Enter a floating viewport (paper-space → model-space-through-viewport).
   * No-op if the current mode is not PSPACE.
   */
  enterViewport: (viewportId: string) => void;
  /**
   * Exit the active floating viewport back to PSPACE.
   * No-op if the current mode is not MSPACE_VIA_VP.
   */
  exitViewport: () => void;
  /** Persist a new tab order (drag-reorder). */
  reorderLayouts: (newOrder: string[]) => void;
  /**
   * Called on document load — seeds layoutOrder from the imported spaces.
   * documentId is used to distinguish a genuine new-file import (which must
   * unconditionally reset tab order/mode/viewport) from a same-document
   * geometry resync (which preserves the user's active space and tab order).
   */
  initFromSpaces: (
    documentId: string,
    spaces: ReadonlyArray<{ id: string; kind: string }>,
  ) => void;
  /**
   * Called when the editorStore's activeSpaceId or layoutContext changes
   * (via useEffect in App.tsx) so the two stores stay in sync without
   * creating a circular import.
   */
  syncFromEditor: (
    activeSpaceId: string,
    layoutContext: "model" | "paper" | "viewport",
  ) => void;
}

export const useSpaceContextStore = create<SpaceContextState>((set, get) => ({
  activeSpaceId: "model",
  activeViewportId: null,
  mode: "MSPACE",
  layoutOrder: ["model"],
  lastInitDocumentId: null,

  setActiveSpace: (spaceId) => {
    set({
      activeSpaceId: spaceId,
      activeViewportId: null,
      mode: spaceId === "model" ? "MSPACE" : "PSPACE",
    });
  },

  enterViewport: (viewportId) => {
    if (get().mode !== "PSPACE") return;
    set({ mode: "MSPACE_VIA_VP", activeViewportId: viewportId });
  },

  exitViewport: () => {
    if (get().mode !== "MSPACE_VIA_VP") return;
    set({ mode: "PSPACE", activeViewportId: null });
  },

  reorderLayouts: (newOrder) => {
    set({ layoutOrder: newOrder });
  },

  initFromSpaces: (documentId, spaces) => {
    const ids = spaces
      .filter((s) => s.kind === "model" || s.kind === "layout")
      .map((s) => s.id);
    const order = ids.length > 0 ? ids : ["model"];
    const current = get();

    // New document import — space ids like "model"/"layout-1" are reused across
    // different files, so we cannot rely on id-overlap to detect replacement.
    // Unconditionally reinitialize when documentId changes.
    if (documentId !== current.lastInitDocumentId) {
      set({
        lastInitDocumentId: documentId,
        layoutOrder: order,
        activeSpaceId: order[0] ?? "model",
        mode: "MSPACE",
        activeViewportId: null,
      });
      return;
    }

    // Same document resync (geometry command / layout add-delete).
    // Preserve the user's active space and reordered tabs; only fall back
    // to model/MSPACE if the currently active space was deleted.
    const currentStillValid = order.includes(current.activeSpaceId);
    if (currentStillValid) {
      // Merge: preserve user-reordered tabs for spaces still present,
      // append newly-added spaces at the end, drop removed spaces.
      const incomingSet = new Set(order);
      const preserved = current.layoutOrder.filter((id) => incomingSet.has(id));
      const preservedSet = new Set(preserved);
      const appended = order.filter((id) => !preservedSet.has(id));
      set({ layoutOrder: [...preserved, ...appended] });
    } else {
      // Active space was deleted from the current document — reset to model.
      set({
        layoutOrder: order,
        activeSpaceId: order[0] ?? "model",
        mode: "MSPACE",
        activeViewportId: null,
      });
    }
  },

  syncFromEditor: (activeSpaceId, layoutContext) => {
    const current = get();
    // Derive mode from the editor state. MSPACE_VIA_VP is not set here
    // (that comes from enterViewport called by Scene3DStage in MC3).
    const nextMode: SpaceContextMode =
      layoutContext === "viewport"
        ? "MSPACE_VIA_VP"
        : layoutContext === "model"
          ? "MSPACE"
          : "PSPACE";

    if (
      current.activeSpaceId === activeSpaceId &&
      current.mode === nextMode
    ) {
      return;
    }
    set({
      activeSpaceId,
      mode: nextMode,
      activeViewportId: nextMode !== "MSPACE_VIA_VP" ? null : current.activeViewportId,
    });
  },
}));
